import { Canvas2DReplayer, Canvas2DResourceRegistry, RasterTileCache, } from "@dopejs/pingo-backend-canvas2d";
import { createRoot, decodeMutationBatch, } from "@dopejs/pingo-reconciler";
import { decodeEditStream, decodeEventTransactionBatch, decodeInputBatch, } from "@dopejs/pingo-editing";
import { encodeSystemTextMetricBatch } from "./system-text-metrics";
import { MediaPipeline } from "./media";
import { FRAME_DIAGNOSTICS_ANIMATION_ACTIVE_INDEX, FRAME_DIAGNOSTICS_ANIMATION_CANCELS_INDEX, FRAME_DIAGNOSTICS_ANIMATION_LAYOUT_NODES_INDEX, FRAME_DIAGNOSTICS_ANIMATION_PHASE_ACTIVE_INDEX, FRAME_DIAGNOSTICS_ANIMATION_PHASE_AFTER_INDEX, FRAME_DIAGNOSTICS_ANIMATION_PHASE_BEFORE_INDEX, FRAME_DIAGNOSTICS_ANIMATION_PRESENTATION_CHANGES_INDEX, FRAME_DIAGNOSTICS_ANIMATION_RETAINED_BYTES_INDEX, FRAME_DIAGNOSTICS_ANIMATION_RETARGETS_INDEX, FRAME_DIAGNOSTICS_ANIMATION_SAMPLED_FRAMES_INDEX, FRAME_DIAGNOSTICS_ANIMATION_STARTS_INDEX, FRAME_DIAGNOSTICS_DIRTY_HIT_NODES_INDEX, FRAME_DIAGNOSTICS_DIRTY_LAYOUT_NODES_INDEX, FRAME_DIAGNOSTICS_DIRTY_PAINT_NODES_INDEX, FRAME_DIAGNOSTICS_DIRTY_PAINT_SELF_NODES_INDEX, FRAME_DIAGNOSTICS_DIRTY_SEMANTICS_NODES_INDEX, FRAME_DIAGNOSTICS_DISPLAY_COMMANDS_INDEX, FRAME_DIAGNOSTICS_FRAME_SEQ_INDEX, FRAME_DIAGNOSTICS_INTERACTION_STATE_CHANGES_INDEX, FRAME_DIAGNOSTICS_LAYOUT_CHANGED_NODES_INDEX, FRAME_DIAGNOSTICS_LAYOUT_VISITED_NODES_INDEX, FRAME_DIAGNOSTICS_OVER_INVALIDATED_FRAMES_INDEX, FRAME_DIAGNOSTICS_PAINT_REBUILT_INDEX, FRAME_DIAGNOSTICS_PICTURE_BUILDS_INDEX, FRAME_DIAGNOSTICS_PICTURE_BUDGET_FALLBACKS_INDEX, FRAME_DIAGNOSTICS_OBSERVE_GEOMETRY_REJECTED_INDEX, LAYOUT_GEOMETRY_HEADER_FRAME_SEQ_INDEX, LAYOUT_GEOMETRY_HEADER_RECORD_COUNT_INDEX, LAYOUT_GEOMETRY_HEADER_VERSION_INDEX, LAYOUT_GEOMETRY_HEADER_WORDS, LAYOUT_GEOMETRY_RECORD_CLIP_LEFT_BITS_INDEX, LAYOUT_GEOMETRY_RECORD_FLAGS_INDEX, LAYOUT_GEOMETRY_RECORD_NODE_ID_INDEX, LAYOUT_GEOMETRY_RECORD_OWN_LEFT_BITS_INDEX, LAYOUT_GEOMETRY_RECORD_WORDS, LAYOUT_GEOMETRY_VERSION, FRAME_DIAGNOSTICS_PICTURE_CACHE_HITS_INDEX, FRAME_DIAGNOSTICS_PICTURE_DEFINES_INDEX, FRAME_DIAGNOSTICS_PICTURE_HASH_HIGH_INDEX, FRAME_DIAGNOSTICS_PICTURE_RELEASES_INDEX, FRAME_DIAGNOSTICS_PICTURE_RESIDENT_BYTES_INDEX, FRAME_DIAGNOSTICS_PICTURE_RESIDENT_COUNT_INDEX, FRAME_DIAGNOSTICS_PICTURE_RESOURCE_BYTES_INDEX, FRAME_DIAGNOSTICS_PRODUCER_ABI_VERSION_INDEX, FRAME_DIAGNOSTICS_SKIPPED_INSTRUCTIONS_INDEX, FRAME_DIAGNOSTICS_VIRTUAL_MATERIALIZED_END_INDEX, FRAME_DIAGNOSTICS_VIRTUAL_MATERIALIZED_START_INDEX, FRAME_DIAGNOSTICS_VIRTUAL_VISIBLE_END_INDEX, FRAME_DIAGNOSTICS_VIRTUAL_VISIBLE_START_INDEX, FRAME_DIAGNOSTICS_VISIBLE_PLACEHOLDERS_INDEX, FRAME_DIAGNOSTICS_PICTURE_HASH_LOW_INDEX, FRAME_DIAGNOSTICS_PICTURE_SUBTREE_BUILDS_INDEX, FRAME_DIAGNOSTICS_PICTURE_SUBTREE_CACHE_HITS_INDEX, FRAME_DIAGNOSTICS_SCENE_NODES_INDEX, FRAME_DIAGNOSTICS_VERSION, FRAME_DIAGNOSTICS_VERSION_INDEX, FRAME_DIAGNOSTICS_WORDS, VIRTUAL_REFILL_HEADER_REQUEST_COUNT_INDEX, VIRTUAL_REFILL_HEADER_VERSION_INDEX, VIRTUAL_REFILL_HEADER_WORDS, VIRTUAL_REFILL_RECORD_END_INDEX, VIRTUAL_REFILL_RECORD_NODE_ID_INDEX, VIRTUAL_REFILL_RECORD_START_INDEX, VIRTUAL_REFILL_RECORD_WORDS, VIRTUAL_REFILL_VERSION, EDITING_GEOMETRY_CHARACTER_END_INDEX, EDITING_GEOMETRY_CHARACTER_LEFT_BITS_INDEX, EDITING_GEOMETRY_CHARACTER_START_INDEX, EDITING_GEOMETRY_CHARACTER_WORDS, EDITING_GEOMETRY_HEADER_CHARACTER_COUNT_INDEX, EDITING_GEOMETRY_HEADER_NODE_ID_INDEX, EDITING_GEOMETRY_HEADER_SELECTION_END_INDEX, EDITING_GEOMETRY_HEADER_SELECTION_START_INDEX, EDITING_GEOMETRY_HEADER_VERSION_INDEX, EDITING_GEOMETRY_HEADER_WORDS, EDITING_GEOMETRY_RECT_HEIGHT_BITS_INDEX, EDITING_GEOMETRY_RECT_LEFT_BITS_INDEX, EDITING_GEOMETRY_RECT_TOP_BITS_INDEX, EDITING_GEOMETRY_RECT_WIDTH_BITS_INDEX, EDITING_GEOMETRY_RECT_WORDS, EDITING_GEOMETRY_VERSION, NULL_NODE_ID, SEMANTICS_HEADER_NODE_COUNT_INDEX, SEMANTICS_HEADER_VERSION_INDEX, PAINTED_TEXT_CHANNEL_INLINE_FALLBACK, PAINTED_TEXT_CHANNEL_MASK, PAINTED_TEXT_CHANNEL_SHAPED_RUN, PAINTED_TEXT_CHANNEL_SYSTEM_FALLBACK, PAINTED_TEXT_FLAG_ORIGIN_CLIPPED, PAINTED_TEXT_FLAG_UNATTRIBUTED, PAINTED_TEXT_HEADER_FLAG_TRUNCATED, PAINTED_TEXT_HEADER_FLAGS_INDEX, PAINTED_TEXT_HEADER_RECORD_COUNT_INDEX, PAINTED_TEXT_HEADER_VERSION_INDEX, PAINTED_TEXT_HEADER_WORDS, PAINTED_TEXT_RECORD_FLAGS_INDEX, PAINTED_TEXT_RECORD_NODE_ID_INDEX, PAINTED_TEXT_RECORD_TEXT_BYTES_INDEX, PAINTED_TEXT_RECORD_WORDS, PAINTED_TEXT_RECORD_X_BITS_INDEX, PAINTED_TEXT_RECORD_Y_BITS_INDEX, PAINTED_TEXT_VERSION, SEMANTICS_HEADER_WORDS, SEMANTICS_RECORD_FLAGS_INDEX, SEMANTICS_RECORD_HEIGHT_BITS_INDEX, SEMANTICS_RECORD_LABEL_BYTES_INDEX, SEMANTICS_RECORD_LEFT_BITS_INDEX, SEMANTICS_RECORD_NODE_ID_INDEX, SEMANTICS_RECORD_ROLE_BYTES_INDEX, SEMANTICS_RECORD_TOP_BITS_INDEX, SEMANTICS_RECORD_VALUE_BYTES_INDEX, SEMANTICS_RECORD_WIDTH_BITS_INDEX, SEMANTICS_RECORD_WORDS, SEMANTICS_VERSION, NON_PASSIVE_REGION_HEADER_REGION_COUNT_INDEX, NON_PASSIVE_REGION_HEADER_VERSION_INDEX, NON_PASSIVE_REGION_HEADER_WORDS, NON_PASSIVE_REGION_RECORD_BOTTOM_BITS_INDEX, NON_PASSIVE_REGION_RECORD_FLAGS_INDEX, NON_PASSIVE_REGION_RECORD_LEFT_BITS_INDEX, NON_PASSIVE_REGION_RECORD_RIGHT_BITS_INDEX, NON_PASSIVE_REGION_RECORD_TOP_BITS_INDEX, NON_PASSIVE_REGION_RECORD_WORDS, NON_PASSIVE_REGION_VERSION, } from "./generated";
/** One text instruction a committed frame's paint emitted. */
/** Nothing happened this frame; shared so the common path allocates nothing. */
const EMPTY_EDIT_STREAM = { transactions: [], structure: [], selections: [] };
const CHANNEL_NAMES = {
    [PAINTED_TEXT_CHANNEL_SHAPED_RUN]: "shapedRun",
    [PAINTED_TEXT_CHANNEL_SYSTEM_FALLBACK]: "systemFallback",
    [PAINTED_TEXT_CHANNEL_INLINE_FALLBACK]: "inlineFallback",
};
/**
 * Distinct preedit code points retained per pair.
 *
 * A ceiling rather than a cache policy: the set only grows with characters a
 * person actually composed into one field, and overflow costs correctness only
 * for code points beyond that, which fall back to the estimate.
 */
const MAXIMUM_COMPOSITION_CODE_POINTS = 4096;
/**
 * The text an edit puts into a value, when it carries any.
 *
 * `replace` and `insert` are ordinary typing and paste; the composition pair is
 * IME preedit, which is in no Scene string either.
 */
function insertedText(command) {
    switch (command.type) {
        case "commitComposition":
        case "insert":
        case "replace":
        case "updateComposition": {
            const text = command.text;
            return text === undefined || text.length === 0 ? undefined : { nodeId: command.nodeId, text };
        }
        default:
            return undefined;
    }
}
/** Editable flag bit the Shell sets for a password field. */
const EDITABLE_PASSWORD_FLAG = 1 << 2;
/** The bullet Core paints in place of a password value. */
const PASSWORD_MASK_CODE_POINT = 0x2022;
function sameCodePoints(left, right) {
    if (left === undefined || right === undefined)
        return left === right;
    return left.length === right.length && left.every((value, index) => value === right[index]);
}
/** Transactional bridge from reconciler frames to Core resources and Canvas. */
export class CanvasFrameSink {
    #context;
    #core;
    #resources;
    /** Milliseconds the most recent Core entry point took, for the frame report. */
    #coreMs = 0;
    #replayer;
    #resourceKinds = new Map();
    #nodeParents = new Map();
    #nodeTextPairs = new Map();
    #textPairState = new Map();
    /**
     * Nodes the Shell has made editable.
     *
     * Their text runs are the only ones measured per code point: Core needs real
     * advances to place a caret and to resolve a pointer to a text offset, and
     * measuring every run would put one `measureText` call per distinct code point
     * on the scroll hot path. The reconciler never un-configures an editable node,
     * so entries leave only with the node itself.
     */
    #editableNodes = new Set();
    /**
     * Editable nodes the Shell marked as passwords.
     *
     * Core paints those as a run of U+2022 that is in no Scene string, so the
     * mask needs measuring the same way IME preedit does. Without it the caret
     * sits a fraction of an em past the last dot for every character.
     */
    #passwordNodes = new Set();
    /** Pair keys currently measured with advances, to detect a node turning editable. */
    #advancePairs = new Set();
    /** Code points each pair was last measured with beyond its own string. */
    #pairExtraCodePoints = new Map();
    /**
     * Code points seen in IME preedit text, per string/style pair.
     *
     * Preedit text lives only inside Core's editing session: it is never a Scene
     * string, so measuring the Scene string alone leaves the composing run on the
     * 0.6em estimate and puts the composition underline, the caret and the IME
     * candidate window in the wrong place. They accumulate because a code point
     * already measured costs nothing to keep and re-measuring is a Core round trip.
     */
    #compositionCodePoints = new Map();
    #onFrame;
    #onVirtualRefills;
    #rasterCache;
    #onAsyncError;
    #onEditTransaction;
    #onStructureRequest;
    #onDocumentSelection;
    #onEventTransaction;
    #onNonPassiveRegions;
    #onEditingGeometry;
    #onSemantics;
    #onLayoutGeometry;
    #onPaintedText;
    #fontSet;
    #fontLoadingDone;
    #layoutGeometryActive = false;
    #paintedTextActive = false;
    #devicePixelRatio = 1;
    #lastDisplayList;
    #lastPictureKey;
    #resourceRevision = 0;
    constructor(context, core, onFrame, rasterCache, onVirtualRefills, onAsyncError, onEditTransaction, onEventTransaction, onNonPassiveRegions, onEditingGeometry, onSemantics, incrementalPicturesEnabled = true, 
    // Appended rather than slotted beside onSemantics: this constructor is
    // positional, so inserting there would silently shift every later argument.
    onLayoutGeometry, onPaintedText, onStructureRequest, onDocumentSelection) {
        this.#context = context;
        this.#core = core;
        this.#onFrame = onFrame;
        this.#rasterCache = rasterCache;
        this.#onVirtualRefills = onVirtualRefills;
        this.#onAsyncError = onAsyncError;
        this.#onEditTransaction = onEditTransaction;
        this.#onStructureRequest = onStructureRequest;
        this.#onDocumentSelection = onDocumentSelection;
        this.#onEventTransaction = onEventTransaction;
        this.#onNonPassiveRegions = onNonPassiveRegions;
        this.#onEditingGeometry = onEditingGeometry;
        this.#onSemantics = onSemantics;
        this.#onLayoutGeometry = onLayoutGeometry;
        this.#onPaintedText = onPaintedText;
        this.#resources = new Canvas2DResourceRegistry();
        this.#replayer = new Canvas2DReplayer();
        this.#core.set_incremental_pictures_enabled?.(incrementalPicturesEnabled);
        this.#fontSet = runtimeFontSet();
        this.#fontLoadingDone =
            this.#fontSet === undefined || this.#core.set_system_text_metrics === undefined
                ? undefined
                : () => {
                    try {
                        this.refreshSystemTextMetrics();
                    }
                    catch (cause) {
                        reportAsyncError(toError(cause, "system font metric refresh failed"), this.#onAsyncError);
                    }
                };
        this.setDevicePixelRatio(runtimeDevicePixelRatio());
        if (this.#fontLoadingDone !== undefined) {
            this.#fontSet?.addEventListener("loadingdone", this.#fontLoadingDone);
        }
    }
    /** Commits Core before mutating backend state or touching Canvas pixels. */
    commit(bytes) {
        const { actions, frameSeq, metricDeltas, nextAdvancePairs, nextEditableNodes, nextExtraCodePoints, nextKinds, nextParents, nextPasswordNodes, nextTextPairs, } = this.preflightResources(bytes);
        const metrics = metricDeltas.upserts.length
            ? this.#resources.measureSystemTextPairs(this.#context, actions, metricDeltas.upserts)
            : [];
        const metricBytes = metrics.length === 0 && metricDeltas.releases.length === 0
            ? undefined
            : encodeSystemTextMetricBatch([
                ...metrics.map((metric) => ({ type: "upsert", metric })),
                ...metricDeltas.releases.map((pair) => ({ type: "release", ...pair })),
            ]);
        const coreStart = performance.now();
        const displayList = this.#core.commit(bytes, metricBytes);
        this.#coreMs = performance.now() - coreStart;
        this.emitVirtualRefills();
        this.emitNonPassiveRegions();
        this.emitEditingGeometry();
        this.emitSemantics();
        this.emitLayoutGeometry();
        this.emitPaintedText();
        if (!(displayList instanceof Uint8Array)) {
            throw new TypeError("Core commit must return Uint8Array DisplayList bytes");
        }
        const coreDiagnostics = (this.#onFrame === undefined && this.#rasterCache === undefined) ||
            this.#core.frame_diagnostics === undefined
            ? undefined
            : parseCoreFrameDiagnostics(this.#core.frame_diagnostics(), frameSeq);
        const glyphResources = this.takeGlyphResources();
        if (actions.length > 0 || glyphResources !== undefined) {
            this.#resources.applyResourceTransaction(actions, glyphResources);
        }
        this.applyPictureResources(frameSeq);
        this.#resourceKinds.clear();
        for (const [id, kind] of nextKinds)
            this.#resourceKinds.set(id, kind);
        replaceMap(this.#nodeParents, nextParents);
        replaceMap(this.#nodeTextPairs, nextTextPairs);
        replaceMap(this.#textPairState, countTextPairs(nextTextPairs));
        replaceSet(this.#editableNodes, nextEditableNodes);
        replaceSet(this.#passwordNodes, nextPasswordNodes);
        this.#advancePairs = nextAdvancePairs;
        this.#pairExtraCodePoints = nextExtraCodePoints;
        for (const key of [...this.#compositionCodePoints.keys()]) {
            if (!this.#textPairState.has(key))
                this.#compositionCodePoints.delete(key);
        }
        if (actions.length > 0 || glyphResources !== undefined) {
            this.#resourceRevision = nextSequence(this.#resourceRevision);
            this.#rasterCache?.clear();
        }
        const pictureKey = coreDiagnostics === undefined
            ? undefined
            : `${coreDiagnostics.pictureHash.toString(16)}:${String(this.#resourceRevision)}`;
        // Frame phases are part of the observability contract: without the replay
        // cost split out, a slow frame is indistinguishable between Core producing
        // the list and the backend drawing it.
        const replayStart = performance.now();
        const replay = this.replay(displayList, pictureKey);
        const replayMs = performance.now() - replayStart;
        this.#lastDisplayList = displayList;
        this.#lastPictureKey = pictureKey;
        this.#onFrame?.({
            ...replay.value,
            cause: "mutation",
            inputBytes: 0,
            mutationBytes: bytes.byteLength,
            displayListBytes: displayList.byteLength,
            coreMs: this.#coreMs,
            replayMs,
            ...(coreDiagnostics === undefined ? {} : { core: coreDiagnostics }),
            ...(this.#rasterCache === undefined ? {} : { rasterCache: this.#rasterCache.metrics() }),
            ...(replay.rasterFrame === undefined ? {} : { rasterFrame: replay.rasterFrame }),
        });
        this.emitEditTransactions(this.takeEditTransactions());
    }
    /**
     * Applies several Input Stream transactions and replays the canvas once.
     *
     * A pointing device emits one event per display refresh, and applying each
     * one separately replays the whole canvas for a picture the next event
     * supersedes before it can be seen. When a replay costs more than a frame,
     * that backlog grows for as long as the gesture lasts and the offset keeps
     * catching up long after the fingers stop. Core state still advances for
     * every transaction and the reverse streams still drain; only the
     * intermediate pictures are skipped.
     */
    inputBatch(batches) {
        if (batches.length === 0)
            return null;
        const core = this.#core;
        if (core.input === undefined)
            throw new Error("Core does not implement Input Stream dispatch");
        let latest;
        let inputBytes = 0;
        let coreMs = 0;
        for (const bytes of batches) {
            this.measureComposition(bytes);
            const coreStart = performance.now();
            const displayList = core.input(bytes);
            // Accumulated, not assigned: a batch applies several transactions and
            // reporting only the last one understates the frame's Core cost.
            coreMs += performance.now() - coreStart;
            inputBytes += bytes.byteLength;
            this.emitVirtualRefills();
            this.emitNonPassiveRegions();
            this.emitEditingGeometry();
            this.emitSemantics();
            this.emitLayoutGeometry();
            this.emitPaintedText();
            this.emitEventTransactions(this.takeEventTransactions());
            // Drained per transaction, not per batch: Core refuses an input frame
            // while a reverse stream is still pending, so a batch whose first
            // transaction produced one would make the second fail.
            this.emitEditTransactions(this.takeEditTransactions());
            this.applyDynamicGlyphResources();
            // The final transaction of a burst can be one that draws nothing, such as
            // the end of a drag, so keep the newest picture rather than the newest
            // transaction.
            if (displayList !== undefined) {
                const diagnostics = core.frame_diagnostics === undefined
                    ? undefined
                    : parseCoreFrameDiagnostics(core.frame_diagnostics());
                this.applyPictureResources(diagnostics?.frameSeq);
                latest = displayList;
            }
        }
        this.#coreMs = coreMs;
        // No trailing drain: the loop already drained after the final transaction.
        if (latest === undefined)
            return null;
        return this.acceptDynamicFrame(latest, {
            cause: "input",
            inputBytes,
            mutationBytes: 0,
        });
    }
    /** Applies one Core Input Stream transaction and replays only changed pixels. */
    input(bytes) {
        const core = this.#core;
        if (core.input === undefined)
            throw new Error("Core does not implement Input Stream dispatch");
        this.measureComposition(bytes);
        const coreStart = performance.now();
        const displayList = core.input(bytes);
        this.#coreMs = performance.now() - coreStart;
        this.emitVirtualRefills();
        this.emitNonPassiveRegions();
        this.emitEditingGeometry();
        this.emitSemantics();
        this.emitLayoutGeometry();
        this.emitPaintedText();
        this.emitEventTransactions(this.takeEventTransactions());
        if (displayList === undefined) {
            this.emitEditTransactions(this.takeEditTransactions());
            return null;
        }
        this.applyDynamicGlyphResources();
        const result = this.acceptDynamicFrame(displayList, {
            cause: "input",
            inputBytes: bytes.byteLength,
            mutationBytes: 0,
        });
        this.emitEditTransactions(this.takeEditTransactions());
        return result;
    }
    /** Advances Core-owned animation and replays only when the Picture changes. */
    advance(elapsedSeconds) {
        if (!Number.isFinite(elapsedSeconds) || elapsedSeconds < 0) {
            throw new RangeError("elapsedSeconds must be finite and non-negative");
        }
        const core = this.#core;
        // Nothing to advance and nothing new to draw: the canvas still holds the
        // last accepted frame, so redrawing it would be a full replay that changes
        // no pixel. Callers that genuinely need the canvas repainted -- a resize,
        // a device pixel ratio change, a transport recovery -- ask for it directly.
        if (core.advance === undefined)
            return null;
        const coreStart = performance.now();
        const displayList = core.advance(elapsedSeconds);
        this.#coreMs = performance.now() - coreStart;
        this.emitVirtualRefills();
        this.emitNonPassiveRegions();
        this.emitEditingGeometry();
        this.emitSemantics();
        this.emitLayoutGeometry();
        this.emitPaintedText();
        // Core produced no new picture this tick, so the canvas already shows the
        // right thing. Replaying it again was costing a full canvas redraw on every
        // clock frame of a scroll, which measured as the single largest term in the
        // worker's frame budget.
        if (displayList === undefined)
            return null;
        this.applyDynamicGlyphResources();
        return this.acceptDynamicFrame(displayList, {
            animationDeltaMs: elapsedSeconds * 1000,
            cause: "animation",
            inputBytes: 0,
            mutationBytes: 0,
        });
    }
    /**
     * Resizes the drawing surface and repaints the frame it already accepted.
     *
     * The backing store is set in device pixels while Core lays out in logical
     * ones. Resizing a canvas clears it, so the last frame has to be replayed
     * here: no tick will do it, because a tick that changes nothing no longer
     * draws.
     */
    resize(width, height, devicePixelRatio) {
        if (![width, height, devicePixelRatio].every((value) => Number.isFinite(value) && value > 0)) {
            throw new RangeError("resize dimensions must be positive and finite");
        }
        const canvas = this.#context.canvas;
        canvas.width = Math.max(1, Math.round(width * devicePixelRatio));
        canvas.height = Math.max(1, Math.round(height * devicePixelRatio));
        this.#rasterCache?.clear();
        const reflowed = this.#core.set_viewport?.(width, height);
        if (reflowed === undefined) {
            // Nothing to reflow, but resizing a canvas clears it, so the frame that
            // is already correct still has to be put back.
            this.replayLastFrame();
            return;
        }
        this.applyDynamicGlyphResources();
        this.acceptDynamicFrame(reflowed, { cause: "mutation", inputBytes: 0, mutationBytes: 0 });
    }
    /** Replays the last fully accepted immutable frame for a Worker-owned render tick. */
    replayLastFrame() {
        const displayList = this.#lastDisplayList;
        return displayList === undefined ? null : this.replay(displayList, this.#lastPictureKey).value;
    }
    /** Installs one newest-wins live frame and redraws without a Shell mutation. */
    updateVideoFrame(resourceId, source, path) {
        this.#resources.updateVideoFrame(resourceId, source);
        this.#resourceRevision = nextSequence(this.#resourceRevision);
        this.#rasterCache?.clear();
        const displayList = this.#lastDisplayList;
        if (displayList === undefined)
            return null;
        return this.acceptDynamicFrame(displayList, {
            cause: "media",
            inputBytes: 0,
            mediaPath: path,
            mutationBytes: 0,
        });
    }
    /** Returns a stable snapshot for diagnostics without exposing mutable cache state. */
    rasterCacheMetrics() {
        return this.#rasterCache?.metrics();
    }
    /** Releases browser capability listeners owned by this rendering sink. */
    dispose() {
        if (this.#fontLoadingDone !== undefined) {
            this.#fontSet?.removeEventListener("loadingdone", this.#fontLoadingDone);
        }
    }
    /**
     * Measures the code points an edit introduces before Core sees the command.
     *
     * Core places the caret from per-code-point advances, and the Host measures
     * the ones the Scene string contains. Editing needs no Shell re-render, so
     * everything typed after that is in no Scene string and had no measurement:
     * the caret fell back to an estimate of 0.6em per code point and drifted
     * further right with every keystroke -- 5px per character for a narrow letter
     * like `l`, and it never came back because the value never returns to Scene.
     *
     * Runs only while some node is editable, so an ordinary scrolling frame never
     * decodes its own input batch, and ahead of Core so the caret is right on the
     * very frame the character appears. A pair is remeasured only when the edit
     * introduces a code point Core has not been told about, which is at most once
     * per new character rather than once per keystroke.
     */
    measureComposition(bytes) {
        if (this.#editableNodes.size === 0)
            return;
        const pending = new Map();
        for (const command of decodeInputBatch(bytes).commands) {
            const edit = insertedText(command);
            if (edit === undefined)
                continue;
            const { nodeId, text } = edit;
            const pair = this.#nodeTextPairs.get(nodeId);
            if (pair === undefined || !this.#editableNodes.has(nodeId))
                continue;
            const key = textPairKey(pair);
            let seen = this.#compositionCodePoints.get(key);
            if (seen === undefined) {
                seen = new Set();
                this.#compositionCodePoints.set(key, seen);
            }
            for (const character of text) {
                const codePoint = character.codePointAt(0);
                if (codePoint === undefined || seen.has(codePoint))
                    continue;
                // Bounded so a long session cannot grow the table without limit; the
                // overflow simply keeps the estimate for rarely-typed code points.
                if (seen.size >= MAXIMUM_COMPOSITION_CODE_POINTS)
                    break;
                seen.add(codePoint);
                pending.set(key, pair);
            }
        }
        if (pending.size === 0)
            return;
        const update = this.#core.set_system_text_metrics?.bind(this.#core);
        if (update === undefined)
            return;
        const metrics = this.#resources.measureSystemTextPairs(this.#context, [], [...pending].map(([key, pair]) => ({
            stringId: pair.stringId,
            styleId: pair.styleId,
            measureAdvances: true,
            measureEditingAdvances: true,
            extraCodePoints: this.mergeExtraCodePoints(key),
        })));
        const displayList = update(encodeSystemTextMetricBatch(metrics.map((metric) => ({ type: "upsert", metric }))));
        this.#advancePairs = new Set([...this.#advancePairs, ...pending.keys()]);
        if (displayList === undefined)
            return;
        this.applyDynamicGlyphResources();
        // Accepted rather than discarded: the replayer diffs against the last
        // accepted list, so skipping one would desynchronize incremental replay.
        // The input frame that follows immediately overwrites these pixels.
        this.acceptDynamicFrame(displayList, {
            animationDeltaMs: 0,
            cause: "animation",
            inputBytes: 0,
            mutationBytes: 0,
        });
    }
    /**
     * Folds newly seen preedit code points into what this pair is measured with.
     *
     * Composition arrives between commits, so the committed set is one frame
     * behind; merging here keeps the next commit from remeasuring what this call
     * already covered.
     */
    mergeExtraCodePoints(key) {
        const merged = new Set([
            ...(this.#pairExtraCodePoints.get(key) ?? []),
            ...(this.#compositionCodePoints.get(key) ?? []),
        ]);
        const sorted = [...merged].sort((left, right) => left - right);
        this.#pairExtraCodePoints = new Map([...this.#pairExtraCodePoints, [key, sorted]]);
        return sorted;
    }
    /** Remeasures every active fallback pair after browser font availability changes. */
    refreshSystemTextMetrics() {
        const update = this.#core.set_system_text_metrics?.bind(this.#core);
        // Newly available fonts change what the same font string measures to, so
        // the remeasure pass cannot reuse anything it learned before them.
        this.#resources.clearMeasurementMemo();
        if (update === undefined || this.#textPairState.size === 0)
            return null;
        const pairs = [...this.#textPairState.entries()].map(([key, { stringId, styleId }]) => ({
            stringId,
            styleId,
            measureAdvances: true,
            measureEditingAdvances: this.#advancePairs.has(key),
            extraCodePoints: [...(this.#pairExtraCodePoints.get(key) ?? [])],
        }));
        const metrics = this.#resources.measureSystemTextPairs(this.#context, [], pairs);
        const displayList = update(encodeSystemTextMetricBatch(metrics.map((metric) => ({ type: "upsert", metric }))));
        if (displayList === undefined)
            return null;
        this.applyDynamicGlyphResources();
        return this.acceptDynamicFrame(displayList, {
            animationDeltaMs: 0,
            cause: "animation",
            inputBytes: 0,
            mutationBytes: 0,
        });
    }
    /** Invalidates DPR-sensitive raster entries without changing Core logical coordinates. */
    setDevicePixelRatio(value) {
        if (!Number.isFinite(value) || value <= 0)
            throw new RangeError("devicePixelRatio must be positive");
        if (value === this.#devicePixelRatio)
            return;
        this.#devicePixelRatio = value;
        this.#rasterCache?.clear();
        this.refreshSystemTextMetrics();
        const displayList = this.#core.set_device_pixel_ratio?.(value);
        if (displayList === undefined)
            return;
        this.applyDynamicGlyphResources();
        this.acceptDynamicFrame(displayList, {
            animationDeltaMs: 0,
            cause: "animation",
            inputBytes: 0,
            mutationBytes: 0,
        });
    }
    /** Applies a live accessibility preference and replays only if presentation changes. */
    setReducedMotion(value) {
        const displayList = this.#core.set_reduced_motion?.(value);
        if (displayList === undefined)
            return;
        this.applyDynamicGlyphResources();
        this.acceptDynamicFrame(displayList, {
            animationDeltaMs: 0,
            cause: "animation",
            inputBytes: 0,
            mutationBytes: 0,
        });
    }
    replay(displayList, pictureKey) {
        const cache = this.#rasterCache;
        if (cache === undefined || pictureKey === undefined) {
            // Every frame is a full repaint: Core emits no damage rectangles and the
            // DisplayList has no clear command, so the previous frame's pixels have
            // to go before this one replays. Without this, anything that shrinks,
            // moves, or disappears -- a collapsed section, a scrolled row, a hover
            // state that drops a background -- stays on the canvas underneath the new
            // frame. The raster-cache paths below already clear their own target.
            this.clearSurface();
            return { value: this.replayScaled(this.#context, displayList) };
        }
        const canvas = this.#context.canvas;
        const result = cache.render(this.#context, {
            devicePixelRatio: this.#devicePixelRatio,
            height: canvas.height,
            pictureKey,
            width: canvas.width,
        }, (context) => this.replayScaled(context, displayList));
        return {
            rasterFrame: { bypassed: result.bypassed, hits: result.hits, misses: result.misses },
            value: result.value,
        };
    }
    /**
     * Drops the presented frame in device pixels.
     *
     * `resetTransform` because the backing-store scale is applied per replay and
     * a caller may have left one behind: the clear has to cover the whole surface
     * regardless of what the last frame was drawn through.
     */
    clearSurface() {
        const context = this.#context;
        const canvas = context.canvas;
        context.save();
        try {
            context.resetTransform();
            context.clearRect(0, 0, canvas.width, canvas.height);
        }
        finally {
            context.restore();
        }
    }
    /**
     * Replays one DisplayList in logical pixels.
     *
     * DisplayList coordinates are logical (CSS) pixels and glyph masks are
     * rasterized at the device pixel ratio, so the backing store scale belongs
     * here: without it logical units land one-to-one on device pixels and the
     * whole scene renders at 1/ratio of its intended size on HiDPI displays.
     */
    replayScaled(context, displayList) {
        const ratio = this.#devicePixelRatio;
        if (ratio === 1)
            return this.#replayer.replay(context, displayList, this.#resources);
        context.save();
        context.scale(ratio, ratio);
        try {
            return this.#replayer.replay(context, displayList, this.#resources);
        }
        finally {
            context.restore();
        }
    }
    acceptDynamicFrame(displayList, source) {
        if (!(displayList instanceof Uint8Array)) {
            throw new TypeError("Core dynamic frame must return Uint8Array DisplayList bytes");
        }
        const coreDiagnostics = this.#core.frame_diagnostics === undefined
            ? undefined
            : parseCoreFrameDiagnostics(this.#core.frame_diagnostics());
        this.applyPictureResources(coreDiagnostics?.frameSeq);
        const pictureKey = coreDiagnostics === undefined
            ? undefined
            : `${coreDiagnostics.pictureHash.toString(16)}:${String(this.#resourceRevision)}`;
        // Timed for the same reason the mutation path is: without the replay cost
        // split out, a slow frame is ambiguous between Core producing the list and
        // the backend drawing it. This is the path a Core-owned scroll, an
        // animation and an input frame all take -- the frames whose budget is
        // tightest -- so leaving it untimed put the observability contract on the
        // half that is not under pressure. `coreMs` comes from whichever advance
        // produced this list, recorded by the caller.
        const replayStart = performance.now();
        const replay = this.replay(displayList, pictureKey);
        const replayMs = performance.now() - replayStart;
        this.#lastDisplayList = displayList;
        this.#lastPictureKey = pictureKey;
        this.#onFrame?.({
            ...replay.value,
            ...source,
            displayListBytes: displayList.byteLength,
            coreMs: this.#coreMs,
            replayMs,
            ...(coreDiagnostics === undefined ? {} : { core: coreDiagnostics }),
            ...(this.#rasterCache === undefined ? {} : { rasterCache: this.#rasterCache.metrics() }),
            ...(replay.rasterFrame === undefined ? {} : { rasterFrame: replay.rasterFrame }),
        });
        return replay.value;
    }
    preflightResources(bytes) {
        const batch = decodeMutationBatch(bytes);
        const nextKinds = new Map(this.#resourceKinds);
        const nextParents = new Map(this.#nodeParents);
        const nextTextPairs = new Map(this.#nodeTextPairs);
        const nextEditableNodes = new Set(this.#editableNodes);
        const nextPasswordNodes = new Set(this.#passwordNodes);
        const children = indexChildren(nextParents);
        const actions = [];
        for (const mutation of batch.mutations) {
            if (mutation.type === "createNode") {
                nextParents.set(mutation.nodeId, mutation.parent);
                addChild(children, mutation.parent, mutation.nodeId);
            }
            else if (mutation.type === "removeNode") {
                removeHostSubtree(mutation.nodeId, nextParents, nextTextPairs, nextEditableNodes, nextPasswordNodes, children);
            }
            else if (mutation.type === "reparent") {
                const previousParent = nextParents.get(mutation.nodeId);
                if (previousParent !== undefined)
                    removeChild(children, previousParent, mutation.nodeId);
                nextParents.set(mutation.nodeId, mutation.newParent);
                addChild(children, mutation.newParent, mutation.nodeId);
            }
            else if (mutation.type === "setTextRun" || mutation.type === "setRichText") {
                nextTextPairs.set(mutation.nodeId, Object.freeze({ stringId: mutation.stringId, styleId: mutation.styleId }));
            }
            else if (mutation.type === "configureEditable") {
                nextEditableNodes.add(mutation.nodeId);
                if ((mutation.flags & EDITABLE_PASSWORD_FLAG) === 0) {
                    nextPasswordNodes.delete(mutation.nodeId);
                }
                else {
                    nextPasswordNodes.add(mutation.nodeId);
                }
            }
            else if (mutation.type === "defineResource") {
                if (nextKinds.has(mutation.resourceId)) {
                    throw new Error(`resource ${String(mutation.resourceId)} is already defined in host`);
                }
                nextKinds.set(mutation.resourceId, mutation.kind);
                actions.push({
                    type: "define",
                    id: mutation.resourceId,
                    kind: mutation.kind,
                    bytes: mutation.bytes,
                });
            }
            else if (mutation.type === "releaseResource") {
                const kind = nextKinds.get(mutation.resourceId);
                if (kind === undefined) {
                    throw new Error(`resource ${String(mutation.resourceId)} is not defined in host`);
                }
                nextKinds.delete(mutation.resourceId);
                actions.push({ type: "release", id: mutation.resourceId, kind });
            }
        }
        const nextPairState = countTextPairs(nextTextPairs);
        const nextAdvancePairs = new Set();
        const nextExtraCodePoints = new Map();
        for (const [nodeId, pair] of nextTextPairs) {
            if (!nextEditableNodes.has(nodeId))
                continue;
            const key = textPairKey(pair);
            nextAdvancePairs.add(key);
            const extra = new Set(this.#compositionCodePoints.get(key) ?? []);
            // The mask is Core's, not the Shell's: it replaces the value at paint
            // time and never becomes a string resource, so nothing else measures it.
            if (nextPasswordNodes.has(nodeId))
                extra.add(PASSWORD_MASK_CODE_POINT);
            nextExtraCodePoints.set(key, [...extra].sort((left, right) => left - right));
        }
        const upserts = [...nextPairState.entries()]
            // A pair is remeasured when it is new, when a node turning editable means
            // Core now needs advances the last measurement omitted, and when the set
            // of code points beyond the string itself changed.
            .filter(([key]) => !this.#textPairState.has(key) ||
            nextAdvancePairs.has(key) !== this.#advancePairs.has(key) ||
            !sameCodePoints(nextExtraCodePoints.get(key), this.#pairExtraCodePoints.get(key)))
            .map(([key, { stringId, styleId }]) => ({
            stringId,
            styleId,
            // Every fallback pair, not just the editable ones: Core wraps text from
            // these advances, and estimating them mis-measures any script whose
            // glyphs are not Latin-width.
            measureAdvances: true,
            measureEditingAdvances: nextAdvancePairs.has(key),
            extraCodePoints: nextExtraCodePoints.get(key) ?? [],
        }));
        const releases = [...this.#textPairState.entries()]
            .filter(([key]) => !nextPairState.has(key))
            .map(([, { stringId, styleId }]) => ({ stringId, styleId }));
        upserts.sort(compareTextPair);
        releases.sort(compareTextPair);
        return {
            actions,
            frameSeq: batch.frameSeq,
            metricDeltas: { releases, upserts },
            nextAdvancePairs,
            nextEditableNodes,
            nextExtraCodePoints,
            nextKinds,
            nextParents,
            nextPasswordNodes,
            nextTextPairs,
        };
    }
    emitVirtualRefills() {
        const core = this.#core;
        if (core.take_virtual_refills === undefined)
            return;
        const requests = parseVirtualRefills(core.take_virtual_refills());
        if (requests.length > 0)
            this.#onVirtualRefills?.(requests);
    }
    takeGlyphResources() {
        if (this.#core.take_glyph_resources === undefined)
            return undefined;
        const bytes = this.#core.take_glyph_resources();
        if (!(bytes instanceof Uint8Array)) {
            throw new TypeError("Core glyph resources must be Uint8Array bytes");
        }
        return bytes.byteLength === 0 ? undefined : bytes;
    }
    applyDynamicGlyphResources() {
        const bytes = this.takeGlyphResources();
        if (bytes === undefined)
            return;
        this.#resources.applyResourceTransaction([], bytes);
        this.#resourceRevision = nextSequence(this.#resourceRevision);
        this.#rasterCache?.clear();
    }
    applyPictureResources(frameSeq) {
        const core = this.#core;
        if (core.take_picture_resources === undefined)
            return;
        const bytes = core.take_picture_resources();
        if (!(bytes instanceof Uint8Array)) {
            throw new TypeError("Core Picture resources must be Uint8Array bytes");
        }
        if (bytes.byteLength === 0)
            return;
        if (frameSeq === undefined) {
            throw new Error("Core Picture transaction requires frame diagnostics");
        }
        if (core.acknowledge_picture_resources === undefined) {
            throw new Error("Core Picture transaction requires acknowledgement support");
        }
        this.#resources.applyPictureResourceBatch(bytes);
        core.acknowledge_picture_resources(frameSeq);
        this.#resourceRevision = nextSequence(this.#resourceRevision);
        this.#rasterCache?.clear();
    }
    takeEditTransactions() {
        if (this.#core.take_edit_transactions === undefined)
            return EMPTY_EDIT_STREAM;
        const bytes = this.#core.take_edit_transactions();
        if (!(bytes instanceof Uint8Array)) {
            throw new TypeError("Core edit transactions must be Uint8Array bytes");
        }
        // The whole stream, not just its transactions: Core reports the structural
        // edits it predicted and the selections it moved on the same batch, and a
        // Shell that decoded only the transactions would be deciding schema
        // questions it was never asked and drawing a toolbar for a caret it could
        // not see.
        return bytes.byteLength === 0 ? EMPTY_EDIT_STREAM : decodeEditStream(bytes);
    }
    emitEditTransactions(stream) {
        for (const transaction of stream.transactions)
            this.#onEditTransaction?.(transaction);
        for (const request of stream.structure)
            this.#onStructureRequest?.(request);
        for (const report of stream.selections)
            this.#onDocumentSelection?.(report);
    }
    takeEventTransactions() {
        if (this.#core.take_event_transactions === undefined)
            return [];
        const bytes = this.#core.take_event_transactions();
        if (!(bytes instanceof Uint8Array)) {
            throw new TypeError("Core event transactions must be Uint8Array bytes");
        }
        return bytes.byteLength === 0 ? [] : decodeEventTransactionBatch(bytes);
    }
    emitEventTransactions(transactions) {
        for (const transaction of transactions)
            this.#onEventTransaction?.(transaction);
    }
    emitNonPassiveRegions() {
        const snapshot = this.#core.non_passive_regions?.();
        if (snapshot === undefined)
            return;
        const regions = parseNonPassiveRegions(snapshot);
        this.#onNonPassiveRegions?.(regions);
    }
    emitEditingGeometry() {
        if (this.#onEditingGeometry === undefined)
            return;
        const snapshot = this.#core.editing_geometry?.();
        if (snapshot === undefined)
            return;
        const frame = parseEditingGeometry(snapshot);
        if (frame !== undefined)
            this.#onEditingGeometry(frame);
    }
    /**
     * Turns the per-frame geometry export on only while something is observed.
     *
     * Nothing measures anything in most applications, and the export is a WASM
     * call plus an allocation per frame; leaving it running would charge every
     * caller for a feature they do not use.
     */
    setLayoutGeometryActive(active) {
        this.#layoutGeometryActive = active;
    }
    emitLayoutGeometry() {
        if (!this.#layoutGeometryActive || this.#onLayoutGeometry === undefined)
            return;
        const snapshot = this.#core.layout_geometry?.();
        if (snapshot === undefined)
            return;
        // An empty frame still carries frameSeq, and a consumer needs it to notice
        // that its observed node stopped being reported.
        this.#onLayoutGeometry(parseLayoutGeometry(snapshot));
    }
    emitSemantics() {
        if (this.#onSemantics === undefined)
            return;
        const snapshot = this.#core.semantics?.();
        if (snapshot === undefined)
            return;
        this.#onSemantics(parseSemantics(snapshot));
    }
    /**
     * Opts into the painted-text render oracle.
     *
     * Off by default and computed only while on. Core records nothing during
     * paint, so an inactive probe costs a frame exactly nothing.
     */
    setPaintedTextActive(active) {
        this.#paintedTextActive = active;
    }
    emitPaintedText() {
        if (!this.#paintedTextActive || this.#onPaintedText === undefined)
            return;
        const snapshot = this.#core.painted_text?.();
        if (snapshot === undefined)
            return;
        this.#onPaintedText(parsePaintedText(snapshot));
    }
}
/** Four-screen default budget; callers can replace it for device-specific policy. */
export function createDefaultRasterCache(context, onError) {
    const pixels = context.canvas.width * context.canvas.height;
    const budgetBytes = Math.min(1024 * 1024 * 1024, Math.max(4, pixels * 4 * 4));
    return new RasterTileCache({
        budgetBytes,
        ...(onError === undefined ? {} : { onError }),
    });
}
function nextSequence(value) {
    const next = (value + 1) >>> 0;
    return next === 0 ? 1 : next;
}
function textPairKey(pair) {
    return `${String(pair.stringId)}:${String(pair.styleId)}`;
}
function compareTextPair(left, right) {
    return left.stringId - right.stringId || left.styleId - right.styleId;
}
function countTextPairs(runs) {
    const pairs = new Map();
    for (const pair of runs.values()) {
        const key = textPairKey(pair);
        const previous = pairs.get(key);
        pairs.set(key, Object.freeze({
            ...pair,
            references: (previous?.references ?? 0) + 1,
        }));
    }
    return pairs;
}
function indexChildren(parents) {
    const children = new Map();
    for (const [node, parent] of parents)
        addChild(children, parent, node);
    return children;
}
function addChild(children, parent, node) {
    let siblings = children.get(parent);
    if (siblings === undefined) {
        siblings = new Set();
        children.set(parent, siblings);
    }
    siblings.add(node);
}
function removeChild(children, parent, node) {
    const siblings = children.get(parent);
    siblings?.delete(node);
    if (siblings?.size === 0)
        children.delete(parent);
}
function removeHostSubtree(root, parents, textPairs, editableNodes, passwordNodes, children) {
    const pending = [root];
    while (pending.length > 0) {
        const node = pending.pop();
        if (node === undefined)
            continue;
        const descendants = children.get(node);
        if (descendants !== undefined)
            pending.push(...descendants);
        const parent = parents.get(node);
        if (parent !== undefined)
            removeChild(children, parent, node);
        children.delete(node);
        parents.delete(node);
        textPairs.delete(node);
        editableNodes.delete(node);
        passwordNodes.delete(node);
    }
}
function replaceSet(target, source) {
    target.clear();
    for (const value of source)
        target.add(value);
}
function replaceMap(target, source) {
    target.clear();
    for (const [key, value] of source)
        target.set(key, value);
}
function runtimeFontSet() {
    const scope = globalThis;
    if (scope.fonts !== undefined)
        return scope.fonts;
    return typeof document === "undefined" ? undefined : document.fonts;
}
function toError(cause, message) {
    return cause instanceof Error ? cause : new Error(message, { cause });
}
function reportAsyncError(error, handler) {
    if (handler !== undefined) {
        handler(error);
        return;
    }
    queueMicrotask(() => {
        throw error;
    });
}
function runtimeDevicePixelRatio() {
    const value = globalThis.devicePixelRatio;
    return typeof value === "number" && Number.isFinite(value) && value > 0 ? value : 1;
}
function parseCoreFrameDiagnostics(words, expectedFrameSeq) {
    if (!(words instanceof Uint32Array) || words.length !== FRAME_DIAGNOSTICS_WORDS) {
        throw new TypeError("Core frame diagnostics must use the generated Uint32Array layout");
    }
    if (words[FRAME_DIAGNOSTICS_VERSION_INDEX] !== FRAME_DIAGNOSTICS_VERSION) {
        throw new Error("Core frame diagnostics version is incompatible with Host");
    }
    const frameSeq = requiredWord(words, FRAME_DIAGNOSTICS_FRAME_SEQ_INDEX);
    if (expectedFrameSeq !== undefined && frameSeq !== expectedFrameSeq) {
        throw new Error("Core diagnostics frame sequence mismatch");
    }
    const rebuilt = requiredWord(words, FRAME_DIAGNOSTICS_PAINT_REBUILT_INDEX);
    if (rebuilt > 1)
        throw new Error("Core diagnostics paintRebuilt must be zero or one");
    const low = requiredWord(words, FRAME_DIAGNOSTICS_PICTURE_HASH_LOW_INDEX);
    const high = requiredWord(words, FRAME_DIAGNOSTICS_PICTURE_HASH_HIGH_INDEX);
    return {
        frameSeq,
        sceneNodes: requiredWord(words, FRAME_DIAGNOSTICS_SCENE_NODES_INDEX),
        dirtyLayoutNodes: requiredWord(words, FRAME_DIAGNOSTICS_DIRTY_LAYOUT_NODES_INDEX),
        dirtyPaintNodes: requiredWord(words, FRAME_DIAGNOSTICS_DIRTY_PAINT_NODES_INDEX),
        dirtyPaintSelfNodes: requiredWord(words, FRAME_DIAGNOSTICS_DIRTY_PAINT_SELF_NODES_INDEX),
        dirtyHitNodes: requiredWord(words, FRAME_DIAGNOSTICS_DIRTY_HIT_NODES_INDEX),
        dirtySemanticsNodes: requiredWord(words, FRAME_DIAGNOSTICS_DIRTY_SEMANTICS_NODES_INDEX),
        layoutChangedNodes: requiredWord(words, FRAME_DIAGNOSTICS_LAYOUT_CHANGED_NODES_INDEX),
        layoutVisitedNodes: requiredWord(words, FRAME_DIAGNOSTICS_LAYOUT_VISITED_NODES_INDEX),
        displayCommands: requiredWord(words, FRAME_DIAGNOSTICS_DISPLAY_COMMANDS_INDEX),
        paintRebuilt: rebuilt === 1,
        pictureBuilds: requiredWord(words, FRAME_DIAGNOSTICS_PICTURE_BUILDS_INDEX),
        pictureCacheHits: requiredWord(words, FRAME_DIAGNOSTICS_PICTURE_CACHE_HITS_INDEX),
        pictureSubtreeBuilds: requiredWord(words, FRAME_DIAGNOSTICS_PICTURE_SUBTREE_BUILDS_INDEX),
        pictureSubtreeCacheHits: requiredWord(words, FRAME_DIAGNOSTICS_PICTURE_SUBTREE_CACHE_HITS_INDEX),
        pictureDefines: requiredWord(words, FRAME_DIAGNOSTICS_PICTURE_DEFINES_INDEX),
        pictureReleases: requiredWord(words, FRAME_DIAGNOSTICS_PICTURE_RELEASES_INDEX),
        pictureResidentCount: requiredWord(words, FRAME_DIAGNOSTICS_PICTURE_RESIDENT_COUNT_INDEX),
        pictureResidentBytes: requiredWord(words, FRAME_DIAGNOSTICS_PICTURE_RESIDENT_BYTES_INDEX),
        pictureResourceBytes: requiredWord(words, FRAME_DIAGNOSTICS_PICTURE_RESOURCE_BYTES_INDEX),
        pictureBudgetFallbacks: requiredWord(words, FRAME_DIAGNOSTICS_PICTURE_BUDGET_FALLBACKS_INDEX),
        observeGeometryRejected: requiredWord(words, FRAME_DIAGNOSTICS_OBSERVE_GEOMETRY_REJECTED_INDEX),
        overInvalidatedFrames: requiredWord(words, FRAME_DIAGNOSTICS_OVER_INVALIDATED_FRAMES_INDEX),
        pictureHash: BigInt(low) | (BigInt(high) << 32n),
        visiblePlaceholders: requiredWord(words, FRAME_DIAGNOSTICS_VISIBLE_PLACEHOLDERS_INDEX),
        virtualVisibleStart: requiredWord(words, FRAME_DIAGNOSTICS_VIRTUAL_VISIBLE_START_INDEX),
        virtualVisibleEnd: requiredWord(words, FRAME_DIAGNOSTICS_VIRTUAL_VISIBLE_END_INDEX),
        virtualMaterializedStart: requiredWord(words, FRAME_DIAGNOSTICS_VIRTUAL_MATERIALIZED_START_INDEX),
        virtualMaterializedEnd: requiredWord(words, FRAME_DIAGNOSTICS_VIRTUAL_MATERIALIZED_END_INDEX),
        skippedInstructions: requiredWord(words, FRAME_DIAGNOSTICS_SKIPPED_INSTRUCTIONS_INDEX),
        producerAbiVersion: requiredWord(words, FRAME_DIAGNOSTICS_PRODUCER_ABI_VERSION_INDEX),
        interactionStateChanges: requiredWord(words, FRAME_DIAGNOSTICS_INTERACTION_STATE_CHANGES_INDEX),
        animationActive: requiredWord(words, FRAME_DIAGNOSTICS_ANIMATION_ACTIVE_INDEX),
        animationPhaseBefore: requiredWord(words, FRAME_DIAGNOSTICS_ANIMATION_PHASE_BEFORE_INDEX),
        animationPhaseActive: requiredWord(words, FRAME_DIAGNOSTICS_ANIMATION_PHASE_ACTIVE_INDEX),
        animationPhaseAfter: requiredWord(words, FRAME_DIAGNOSTICS_ANIMATION_PHASE_AFTER_INDEX),
        animationStarts: requiredWord(words, FRAME_DIAGNOSTICS_ANIMATION_STARTS_INDEX),
        animationRetargets: requiredWord(words, FRAME_DIAGNOSTICS_ANIMATION_RETARGETS_INDEX),
        animationCancels: requiredWord(words, FRAME_DIAGNOSTICS_ANIMATION_CANCELS_INDEX),
        animationSampledFrames: requiredWord(words, FRAME_DIAGNOSTICS_ANIMATION_SAMPLED_FRAMES_INDEX),
        animationPresentationChanges: requiredWord(words, FRAME_DIAGNOSTICS_ANIMATION_PRESENTATION_CHANGES_INDEX),
        animationLayoutNodes: requiredWord(words, FRAME_DIAGNOSTICS_ANIMATION_LAYOUT_NODES_INDEX),
        animationRetainedBytes: requiredWord(words, FRAME_DIAGNOSTICS_ANIMATION_RETAINED_BYTES_INDEX),
    };
}
function requiredWord(words, index) {
    const value = words[index];
    if (value === undefined)
        throw new TypeError("Core frame diagnostics are truncated");
    return value;
}
function parseVirtualRefills(words) {
    if (!(words instanceof Uint32Array) || words.length < VIRTUAL_REFILL_HEADER_WORDS) {
        throw new TypeError("Core virtual refills must use the generated Uint32Array layout");
    }
    if (words[VIRTUAL_REFILL_HEADER_VERSION_INDEX] !== VIRTUAL_REFILL_VERSION) {
        throw new Error("Core virtual refill version is incompatible with Host");
    }
    const count = requiredWord(words, VIRTUAL_REFILL_HEADER_REQUEST_COUNT_INDEX);
    const expected = VIRTUAL_REFILL_HEADER_WORDS + count * VIRTUAL_REFILL_RECORD_WORDS;
    if (!Number.isSafeInteger(expected) || words.length !== expected) {
        throw new TypeError("Core virtual refill request count does not match its payload");
    }
    const requests = [];
    for (let record = 0; record < count; record += 1) {
        const offset = VIRTUAL_REFILL_HEADER_WORDS + record * VIRTUAL_REFILL_RECORD_WORDS;
        const nodeId = requiredWord(words, offset + VIRTUAL_REFILL_RECORD_NODE_ID_INDEX);
        const start = requiredWord(words, offset + VIRTUAL_REFILL_RECORD_START_INDEX);
        const end = requiredWord(words, offset + VIRTUAL_REFILL_RECORD_END_INDEX);
        if (start >= end)
            throw new RangeError("Core virtual refill range must be non-empty");
        requests.push({ end, nodeId, start });
    }
    return requests;
}
function parseNonPassiveRegions(words) {
    if (!(words instanceof Uint32Array) || words.length < NON_PASSIVE_REGION_HEADER_WORDS) {
        throw new TypeError("Core non-passive regions must use the generated Uint32Array layout");
    }
    if (words[NON_PASSIVE_REGION_HEADER_VERSION_INDEX] !== NON_PASSIVE_REGION_VERSION) {
        throw new Error("Core non-passive region version is incompatible with Host");
    }
    const count = requiredWord(words, NON_PASSIVE_REGION_HEADER_REGION_COUNT_INDEX);
    const expected = NON_PASSIVE_REGION_HEADER_WORDS + count * NON_PASSIVE_REGION_RECORD_WORDS;
    if (!Number.isSafeInteger(expected) || words.length !== expected) {
        throw new TypeError("Core non-passive region count does not match its payload");
    }
    const scratch = new DataView(new ArrayBuffer(4));
    const float = (bits) => {
        scratch.setUint32(0, bits, true);
        return scratch.getFloat32(0, true);
    };
    const regions = [];
    for (let record = 0; record < count; record += 1) {
        const offset = NON_PASSIVE_REGION_HEADER_WORDS + record * NON_PASSIVE_REGION_RECORD_WORDS;
        const flags = requiredWord(words, offset + NON_PASSIVE_REGION_RECORD_FLAGS_INDEX);
        const left = float(requiredWord(words, offset + NON_PASSIVE_REGION_RECORD_LEFT_BITS_INDEX));
        const top = float(requiredWord(words, offset + NON_PASSIVE_REGION_RECORD_TOP_BITS_INDEX));
        const right = float(requiredWord(words, offset + NON_PASSIVE_REGION_RECORD_RIGHT_BITS_INDEX));
        const bottom = float(requiredWord(words, offset + NON_PASSIVE_REGION_RECORD_BOTTOM_BITS_INDEX));
        if (flags === 0 || flags > 3)
            throw new RangeError("Core non-passive flags are invalid");
        if (![left, top, right, bottom].every(Number.isFinite) || left >= right || top >= bottom) {
            throw new RangeError("Core non-passive region bounds are invalid");
        }
        regions.push({ bottom, flags, left, right, top });
    }
    return regions;
}
function parseEditingGeometry(words) {
    const minimum = EDITING_GEOMETRY_HEADER_WORDS + EDITING_GEOMETRY_RECT_WORDS * 2;
    if (!(words instanceof Uint32Array) || words.length < minimum) {
        throw new TypeError("Core editing geometry must use the generated Uint32Array layout");
    }
    if (words[EDITING_GEOMETRY_HEADER_VERSION_INDEX] !== EDITING_GEOMETRY_VERSION) {
        throw new Error("Core editing geometry version is incompatible with Host");
    }
    const nodeId = requiredWord(words, EDITING_GEOMETRY_HEADER_NODE_ID_INDEX);
    if (nodeId === NULL_NODE_ID)
        return undefined;
    const selectionStart = requiredWord(words, EDITING_GEOMETRY_HEADER_SELECTION_START_INDEX);
    const selectionEnd = requiredWord(words, EDITING_GEOMETRY_HEADER_SELECTION_END_INDEX);
    if (selectionStart > selectionEnd) {
        throw new RangeError("Core editing geometry selection range is inverted");
    }
    const count = requiredWord(words, EDITING_GEOMETRY_HEADER_CHARACTER_COUNT_INDEX);
    const expected = minimum + count * EDITING_GEOMETRY_CHARACTER_WORDS;
    if (!Number.isSafeInteger(expected) || words.length !== expected) {
        throw new TypeError("Core editing geometry character count does not match its payload");
    }
    const scratch = new DataView(new ArrayBuffer(4));
    const float = (bits) => {
        scratch.setUint32(0, bits, true);
        return scratch.getFloat32(0, true);
    };
    const rect = (offset) => {
        const left = float(requiredWord(words, offset + EDITING_GEOMETRY_RECT_LEFT_BITS_INDEX));
        const top = float(requiredWord(words, offset + EDITING_GEOMETRY_RECT_TOP_BITS_INDEX));
        const width = float(requiredWord(words, offset + EDITING_GEOMETRY_RECT_WIDTH_BITS_INDEX));
        const height = float(requiredWord(words, offset + EDITING_GEOMETRY_RECT_HEIGHT_BITS_INDEX));
        if (![left, top, width, height].every(Number.isFinite) || width < 0 || height < 0) {
            throw new RangeError("Core editing geometry rectangle is invalid");
        }
        return { height, left, top, width };
    };
    const controlBounds = rect(EDITING_GEOMETRY_HEADER_WORDS);
    const selectionBounds = rect(EDITING_GEOMETRY_HEADER_WORDS + EDITING_GEOMETRY_RECT_WORDS);
    const characterBounds = [];
    for (let record = 0; record < count; record += 1) {
        const offset = minimum + record * EDITING_GEOMETRY_CHARACTER_WORDS;
        const start = requiredWord(words, offset + EDITING_GEOMETRY_CHARACTER_START_INDEX);
        const end = requiredWord(words, offset + EDITING_GEOMETRY_CHARACTER_END_INDEX);
        if (start >= end)
            throw new RangeError("Core editing geometry character range is empty");
        characterBounds.push({
            end,
            rect: rect(offset + EDITING_GEOMETRY_CHARACTER_LEFT_BITS_INDEX),
            start,
        });
    }
    return { characterBounds, controlBounds, nodeId, selectionBounds, selectionEnd, selectionStart };
}
/** Fully validates one untrusted Core semantics snapshot before use. */
/**
 * Decodes the observed-geometry frame.
 *
 * A trust boundary like every other decoder here: the bytes normally come from
 * this project's Core, which is not a reason to skip validation.
 */
export function parseLayoutGeometry(words) {
    if (!(words instanceof Uint32Array) || words.length < LAYOUT_GEOMETRY_HEADER_WORDS) {
        throw new TypeError("Core layout geometry must use the generated Uint32Array layout");
    }
    if (words[LAYOUT_GEOMETRY_HEADER_VERSION_INDEX] !== LAYOUT_GEOMETRY_VERSION) {
        throw new Error("Core layout geometry version is incompatible with Host");
    }
    const frameSeq = requiredWord(words, LAYOUT_GEOMETRY_HEADER_FRAME_SEQ_INDEX);
    const count = requiredWord(words, LAYOUT_GEOMETRY_HEADER_RECORD_COUNT_INDEX);
    const expected = LAYOUT_GEOMETRY_HEADER_WORDS + count * LAYOUT_GEOMETRY_RECORD_WORDS;
    if (!Number.isSafeInteger(expected) || words.length !== expected) {
        throw new TypeError("Core layout geometry record count does not match its payload");
    }
    const scratch = new DataView(new ArrayBuffer(4));
    // Infinities are meaningful here: an unclipped node reports an unbounded clip
    // box. NaN is not, and would poison every comparison downstream.
    const float = (index) => {
        scratch.setUint32(0, requiredWord(words, index), true);
        const value = scratch.getFloat32(0, true);
        if (Number.isNaN(value))
            throw new RangeError("Core layout geometry contains NaN");
        return value;
    };
    const rect = (offset) => {
        const width = float(offset + 2);
        const height = float(offset + 3);
        if (width < 0 || height < 0) {
            throw new RangeError("Core layout geometry rectangle has a negative extent");
        }
        return { left: float(offset), top: float(offset + 1), width, height };
    };
    const records = [];
    for (let record = 0; record < count; record += 1) {
        const offset = LAYOUT_GEOMETRY_HEADER_WORDS + record * LAYOUT_GEOMETRY_RECORD_WORDS;
        if (requiredWord(words, offset + LAYOUT_GEOMETRY_RECORD_FLAGS_INDEX) !== 0) {
            throw new RangeError("Core layout geometry flags are reserved");
        }
        records.push({
            nodeId: requiredWord(words, offset + LAYOUT_GEOMETRY_RECORD_NODE_ID_INDEX),
            bounds: rect(offset + LAYOUT_GEOMETRY_RECORD_OWN_LEFT_BITS_INDEX),
            clip: rect(offset + LAYOUT_GEOMETRY_RECORD_CLIP_LEFT_BITS_INDEX),
        });
    }
    return { frameSeq, records };
}
export function parseSemantics(bytes) {
    if (!(bytes instanceof Uint8Array) || bytes.byteLength % 4 !== 0) {
        throw new TypeError("Core semantics must use the generated byte layout");
    }
    const headerBytes = SEMANTICS_HEADER_WORDS * 4;
    if (bytes.byteLength < headerBytes) {
        throw new TypeError("Core semantics snapshot is truncated");
    }
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    if (view.getUint32(SEMANTICS_HEADER_VERSION_INDEX * 4, true) !== SEMANTICS_VERSION) {
        throw new Error("Core semantics version is incompatible with Host");
    }
    const count = view.getUint32(SEMANTICS_HEADER_NODE_COUNT_INDEX * 4, true);
    const decoder = new TextDecoder("utf-8", { fatal: true });
    const nodes = [];
    let offset = headerBytes;
    for (let record = 0; record < count; record += 1) {
        if (offset + SEMANTICS_RECORD_WORDS * 4 > bytes.byteLength) {
            throw new TypeError("Core semantics record is truncated");
        }
        const word = (index) => view.getUint32(offset + index * 4, true);
        const float = (index) => view.getFloat32(offset + index * 4, true);
        const flags = word(SEMANTICS_RECORD_FLAGS_INDEX);
        if ((flags & ~0b111) !== 0)
            throw new RangeError("Core semantics flags are reserved");
        const bounds = {
            left: float(SEMANTICS_RECORD_LEFT_BITS_INDEX),
            top: float(SEMANTICS_RECORD_TOP_BITS_INDEX),
            width: float(SEMANTICS_RECORD_WIDTH_BITS_INDEX),
            height: float(SEMANTICS_RECORD_HEIGHT_BITS_INDEX),
        };
        if (![bounds.left, bounds.top, bounds.width, bounds.height].every(Number.isFinite) ||
            bounds.width < 0 ||
            bounds.height < 0) {
            throw new RangeError("Core semantics bounds are invalid");
        }
        const roleBytes = word(SEMANTICS_RECORD_ROLE_BYTES_INDEX);
        const labelBytes = word(SEMANTICS_RECORD_LABEL_BYTES_INDEX);
        const valueBytes = word(SEMANTICS_RECORD_VALUE_BYTES_INDEX);
        const stringStart = offset + SEMANTICS_RECORD_WORDS * 4;
        const stringBytes = roleBytes + labelBytes + valueBytes;
        const paddedEnd = stringStart + stringBytes + ((4 - (stringBytes % 4)) % 4);
        if (stringBytes > bytes.byteLength ||
            paddedEnd > bytes.byteLength ||
            !Number.isSafeInteger(paddedEnd)) {
            throw new TypeError("Core semantics strings overflow the snapshot");
        }
        const text = (start, length) => {
            try {
                return decoder.decode(bytes.subarray(start, start + length));
            }
            catch {
                throw new TypeError("Core semantics string is not valid UTF-8");
            }
        };
        nodes.push({
            bounds,
            focusable: (flags & 1) !== 0,
            focused: (flags & 2) !== 0,
            label: text(stringStart + roleBytes, labelBytes),
            nodeId: word(SEMANTICS_RECORD_NODE_ID_INDEX),
            password: (flags & 4) !== 0,
            role: text(stringStart, roleBytes),
            value: text(stringStart + roleBytes + labelBytes, valueBytes),
        });
        offset = paddedEnd;
    }
    if (offset !== bytes.byteLength) {
        throw new TypeError("Core semantics snapshot has trailing bytes");
    }
    return nodes;
}
/**
 * Decodes the painted-text render oracle.
 *
 * A trust boundary like every other decoder here: the bytes normally come from
 * this project's Core, which is not a reason to skip validation.
 */
export function parsePaintedText(bytes) {
    if (!(bytes instanceof Uint8Array) || bytes.byteLength % 4 !== 0) {
        throw new TypeError("Core painted text must use the generated byte layout");
    }
    const headerBytes = PAINTED_TEXT_HEADER_WORDS * 4;
    if (bytes.byteLength < headerBytes) {
        throw new TypeError("Core painted text snapshot is truncated");
    }
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    if (view.getUint32(PAINTED_TEXT_HEADER_VERSION_INDEX * 4, true) !== PAINTED_TEXT_VERSION) {
        throw new Error("Core painted text version is incompatible with Host");
    }
    const headerFlags = view.getUint32(PAINTED_TEXT_HEADER_FLAGS_INDEX * 4, true);
    if ((headerFlags & ~PAINTED_TEXT_HEADER_FLAG_TRUNCATED) !== 0) {
        throw new RangeError("Core painted text header flags are reserved");
    }
    const count = view.getUint32(PAINTED_TEXT_HEADER_RECORD_COUNT_INDEX * 4, true);
    const decoder = new TextDecoder("utf-8", { fatal: true });
    const records = [];
    let offset = headerBytes;
    for (let record = 0; record < count; record += 1) {
        if (offset + PAINTED_TEXT_RECORD_WORDS * 4 > bytes.byteLength) {
            throw new TypeError("Core painted text record is truncated");
        }
        const word = (index) => view.getUint32(offset + index * 4, true);
        const float = (index) => view.getFloat32(offset + index * 4, true);
        const flags = word(PAINTED_TEXT_RECORD_FLAGS_INDEX);
        const known = PAINTED_TEXT_CHANNEL_MASK | PAINTED_TEXT_FLAG_ORIGIN_CLIPPED | PAINTED_TEXT_FLAG_UNATTRIBUTED;
        if ((flags & ~known) !== 0)
            throw new RangeError("Core painted text flags are reserved");
        const channel = CHANNEL_NAMES[flags & PAINTED_TEXT_CHANNEL_MASK];
        if (channel === undefined)
            throw new RangeError("Core painted text channel is unknown");
        const x = float(PAINTED_TEXT_RECORD_X_BITS_INDEX);
        const y = float(PAINTED_TEXT_RECORD_Y_BITS_INDEX);
        if (Number.isNaN(x) || Number.isNaN(y)) {
            throw new RangeError("Core painted text origin contains NaN");
        }
        const textBytes = word(PAINTED_TEXT_RECORD_TEXT_BYTES_INDEX);
        const start = offset + PAINTED_TEXT_RECORD_WORDS * 4;
        const paddedEnd = start + textBytes + ((4 - (textBytes % 4)) % 4);
        if (!Number.isSafeInteger(paddedEnd) || paddedEnd > bytes.byteLength) {
            throw new TypeError("Core painted text string overflows the snapshot");
        }
        let text;
        try {
            text = decoder.decode(bytes.subarray(start, start + textBytes));
        }
        catch {
            throw new TypeError("Core painted text string is not valid UTF-8");
        }
        records.push({
            channel,
            nodeId: word(PAINTED_TEXT_RECORD_NODE_ID_INDEX),
            origin: { x, y },
            originClipped: (flags & PAINTED_TEXT_FLAG_ORIGIN_CLIPPED) !== 0,
            text,
            unattributed: (flags & PAINTED_TEXT_FLAG_UNATTRIBUTED) !== 0,
        });
        offset = paddedEnd;
    }
    if (offset !== bytes.byteLength) {
        throw new TypeError("Core painted text snapshot has trailing bytes");
    }
    return { records, truncated: (headerFlags & PAINTED_TEXT_HEADER_FLAG_TRUNCATED) !== 0 };
}
/** Creates the deterministic main-thread M1 fallback rendering root. */
export function createCanvasRoot(context, core, options = {}) {
    const coreRoot = { current: undefined };
    let media;
    const sink = new CanvasFrameSink(context, core, options.onFrame === undefined
        ? undefined
        : (report) => options.onFrame?.({
            ...report,
            ...(coreRoot.current === undefined ? {} : { style: coreRoot.current.styleMetrics() }),
        }), undefined, undefined, options.onPostCommitError, (transaction) => {
        coreRoot.current?.applyEditTransaction(transaction);
        options.onEditTransaction?.(transaction);
    }, (transaction) => {
        coreRoot.current?.applyEventTransaction(transaction);
        options.onEventTransaction?.(transaction);
    }, options.onNonPassiveRegions, options.onEditingGeometry, options.onSemantics, options.incrementalPicturesEnabled ?? true, 
    // Observed geometry is activated by the reconciler's observation set, which
    // only the hosted root wires up, so this raw root has nothing to export.
    undefined, options.onPaintedText, options.onStructureRequest, options.onDocumentSelection);
    // The probe is inert until something wants it; asking for the callback is
    // the ask.
    if (options.onPaintedText !== undefined)
        sink.setPaintedTextActive(true);
    const mediaPipeline = () => {
        media ??= new MediaPipeline({
            transferableFrames: false,
            target: {
                submit: (resourceId, source, path) => sink.updateVideoFrame(resourceId, source, path),
            },
            onMetadata: (nodeId, width, height) => coreRoot.current?.updateMediaMetadata(nodeId, width, height),
            onEvent: (nodeId, event) => coreRoot.current?.applyMediaEvent(nodeId, event),
            ...(options.onMediaMetrics === undefined ? {} : { onMetrics: options.onMediaMetrics }),
        });
        return media;
    };
    const root = createRoot(sink, {
        ...options,
        onMediaBinding: (binding, nodeId) => {
            mediaPipeline().bind(binding, nodeId);
            options.onMediaBinding?.(binding, nodeId);
        },
        onInteractionRequest: (request) => {
            if (request.type === "mediaPlay")
                mediaPipeline().play(request.nodeId);
            else if (request.type === "mediaPause")
                mediaPipeline().pause(request.nodeId);
            else if (request.type === "mediaSeek")
                mediaPipeline().seek(request.nodeId, request.timeSeconds);
            options.onInteractionRequest?.(request);
        },
    });
    coreRoot.current = root;
    return {
        render: (node) => root.render(node),
        flushSync: () => root.flushSync(),
        invokeCallback: (callbackId) => root.invokeCallback(callbackId),
        styleMetrics: () => root.styleMetrics(),
        unmount: () => {
            try {
                root.unmount();
            }
            finally {
                media?.close();
                sink.dispose();
            }
        },
        get failed() {
            return root.failed;
        },
    };
}
//# sourceMappingURL=main-thread.js.map